import { ChangeEvent, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  enrollMfa,
  unenrollMfa,
  verifyMfa,
  MfaEnrollResult,
} from "@services/supabaseAPI";
import {
  deleteRecoveryCodes,
  generateRecoveryCodes,
} from "@services/api";

interface Props {
  open: boolean;
  // "enroll": full flow from QR to recovery-codes display.
  // "regenerate": skip enrollment; the user is already verified and
  // we just call generate-codes. Used by the "Regenerate codes" action
  // in the profile MFA panel.
  mode: "enroll" | "regenerate";
  onClose: () => void;
  onComplete: () => void;
}

type Step = "loading" | "scan" | "codes" | "error";

/**
 * Multi-step dialog that walks the user through TOTP enrollment and
 * recovery-code download. Stops in the "codes" step until the user
 * explicitly confirms they've saved them — closing the dialog before
 * that point also rolls back the unverified factor so we don't leak
 * half-enrolled factors into the user's account.
 */
const MfaEnrollDialog = ({ open, mode, onClose, onComplete }: Props) => {
  const [step, setStep] = useState<Step>("loading");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [enrollData, setEnrollData] = useState<MfaEnrollResult | null>(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStep("loading");
    setErrorMsg("");
    setEnrollData(null);
    setCode("");
    setRecoveryCodes([]);
    setSavedConfirmed(false);

    (async () => {
      if (mode === "regenerate") {
        const res = await generateRecoveryCodes();
        if (cancelled) return;
        if (!res.success) {
          setErrorMsg(res.error.message);
          setStep("error");
          return;
        }
        setRecoveryCodes(res.data.codes);
        setStep("codes");
        return;
      }
      // mode === "enroll"
      const res = await enrollMfa("Authenticator");
      if (cancelled) return;
      if (!res.success) {
        setErrorMsg(("error" in res && res.error) || "Could not start MFA enrollment");
        setStep("error");
        return;
      }
      setEnrollData(res.data);
      setStep("scan");
    })();

    return () => {
      cancelled = true;
    };
  }, [open, mode]);

  const handleVerify = async () => {
    if (!enrollData) return;
    setVerifying(true);
    setErrorMsg("");
    const verifyResult = await verifyMfa(enrollData.factorId, code.trim());
    if (!verifyResult.success) {
      setErrorMsg(verifyResult.error || "Verification failed");
      setVerifying(false);
      return;
    }
    // Verify succeeded → session is now AAL2 → server will accept the
    // recovery-code generation request.
    const codesRes = await generateRecoveryCodes();
    setVerifying(false);
    if (!codesRes.success) {
      setErrorMsg(codesRes.error.message);
      return;
    }
    setRecoveryCodes(codesRes.data.codes);
    setStep("codes");
  };

  const handleCancel = async () => {
    // If the user bails out before verifying, scrub the unverified
    // factor so it doesn't linger in their Supabase account.
    if (step === "scan" && enrollData) {
      await unenrollMfa(enrollData.factorId);
      // Best-effort cleanup of any codes that might exist (shouldn't,
      // but guards against an interrupted retry).
      await deleteRecoveryCodes();
    }
    onClose();
  };

  const handleDownload = () => {
    const body = [
      "Annogram recovery codes",
      "",
      "Each code can be used once to sign in if you lose access to",
      "your authenticator app. Keep these somewhere safe.",
      "",
      ...recoveryCodes,
      "",
      `Generated ${new Date().toISOString()}`,
    ].join("\n");
    const blob = new Blob([body], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "annogram-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFinish = () => {
    onComplete();
    onClose();
  };

  const title =
    mode === "regenerate"
      ? "Regenerate recovery codes"
      : "Set up two-factor authentication";

  return (
    <Dialog
      open={open}
      // Don't allow click-out / Esc dismissal at the codes step — the
      // user must explicitly confirm they've saved the codes or
      // they'll be locked out of recovery.
      onClose={(_, reason) => {
        if (step === "codes" && (reason === "backdropClick" || reason === "escapeKeyDown"))
          return;
        handleCancel();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {step === "loading" && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {step === "error" && (
          <Alert severity="error">{errorMsg || "Something went wrong."}</Alert>
        )}

        {step === "scan" && enrollData && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Scan this QR code with an authenticator app (Google Authenticator,
              1Password, Authy, …), then enter the 6-digit code it shows.
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                bgcolor: "background.default",
                p: 2,
                borderRadius: 1,
              }}
            >
              {/* Supabase returns the QR as an SVG data URL */}
              <img
                src={enrollData.qrCode}
                alt="MFA QR code"
                style={{ width: 200, height: 200 }}
              />
            </Box>
            <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
              Can't scan? Enter this secret manually: <strong>{enrollData.secret}</strong>
            </Typography>
            <TextField
              autoFocus
              label="6-digit code"
              value={code}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              error={!!errorMsg}
              helperText={errorMsg || " "}
              fullWidth
            />
          </Stack>
        )}

        {step === "codes" && (
          <Stack spacing={2}>
            <Alert severity="warning">
              Save these codes now. Each one can be used <strong>once</strong> to
              sign in if you lose your authenticator. You won't see them again.
            </Alert>
            <Box
              sx={{
                fontFamily: "monospace",
                bgcolor: "background.default",
                p: 2,
                borderRadius: 1,
                lineHeight: 1.8,
              }}
            >
              {recoveryCodes.map((c) => (
                <Box key={c}>{c}</Box>
              ))}
            </Box>
            <Box>
              <Button onClick={handleDownload} size="small" variant="outlined">
                Download as .txt
              </Button>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={savedConfirmed}
                  onChange={(e) => setSavedConfirmed(e.target.checked)}
                />
              }
              label="I've saved these codes somewhere safe"
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step === "scan" && (
          <>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleVerify}
              disabled={code.length !== 6 || verifying}
            >
              {verifying ? "Verifying…" : "Verify"}
            </Button>
          </>
        )}
        {step === "codes" && (
          <Button
            variant="contained"
            disabled={!savedConfirmed}
            onClick={handleFinish}
          >
            Done
          </Button>
        )}
        {step === "error" && <Button onClick={handleCancel}>Close</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default MfaEnrollDialog;
